import React, { useState, useEffect } from "react";
import { apiClient } from "../../../apiClient";
import { endpoints } from "../../../configs";
import moment from "moment";
import SnowFlakeAttribution from "../../snowFlake/SnowFlakeAttribution";
import CustomDateFilter from "../../../components/base/CustomDateFilter";
import Form from "../../../components/base/Form";
import Toast from "../../../components/base/Toast";
import SnowFlakeInsights from "../../snowFlake/SnowFlakeInsights";
import "../../snowFlake/SnowFlakeDashboard.scss";

const SnowflakeTab = ({ partnerId }) => {
  const [loading, setLoading] = useState(false);
  const [attributionData, setAttributionData] = useState([]);
  const [correlateData, setCorrelateData] = useState([]);
  const [startDate, setStartDate] = useState(
    moment()
      .subtract(30, "days")
      .format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  // Pagination for attribution table
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [tableLoading, setTableLoading] = useState(false);

  const fetchFullData = async () => {
    setLoading(true);
    try {
      const start = moment(startDate)
        .startOf("day")
        .toISOString();
      const end = moment(endDate)
        .endOf("day")
        .toISOString();

      const [spikesRes, attrRes, corrRes] = await Promise.all([
        apiClient.get(
          `${
            endpoints().snowFlakeAPI
          }/spikes?start=${start}&end=${end}&partnerId=${partnerId}`
        ),
        apiClient.get(
          `${
            endpoints().snowFlakeAPI
          }/attribution?start=${start}&end=${end}&partnerId=${partnerId}`
        ),
        apiClient.get(
          `${
            endpoints().snowFlakeAPI
          }/correlate?start=${start}&end=${end}&partnerId=${partnerId}&page=1&pageSize=${pageSize}`
        )
      ]);

      setAttributionData((attrRes.data && attrRes.data.data) || []);
      setCorrelateData((corrRes.data && corrRes.data.correlations) || []);
      setTotalRecords((corrRes.data && corrRes.data.totalCount) || 0);
      setCurrentPage(1);
    } catch (err) {
      Toast.error(
        "Error fetching Snowflake data: " +
          (err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCorrelationData = async (page = currentPage) => {
    setTableLoading(true);
    try {
      const start = moment(startDate)
        .startOf("day")
        .toISOString();
      const end = moment(endDate)
        .endOf("day")
        .toISOString();

      const res = await apiClient.get(
        `${
          endpoints().snowFlakeAPI
        }/correlate?start=${start}&end=${end}&partnerId=${partnerId}&page=${page}&pageSize=${pageSize}`
      );
      setCorrelateData((res.data && res.data.correlations) || []);
      setTotalRecords((res.data && res.data.totalCount) || 0);
    } catch (err) {
      Toast.error(
        "Error fetching correlations: " +
          (err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : err.message)
      );
    } finally {
      setTableLoading(false);
    }
  };

  // Full Refresh on Filter Change
  useEffect(() => {
    if (partnerId) {
      fetchFullData();
    }
  }, [partnerId, startDate, endDate]);

  // Targeted Refresh on Pagination
  useEffect(() => {
    if (partnerId && !loading && currentPage > 1) {
      fetchCorrelationData(currentPage);
    }
  }, [currentPage]);

  // Targeted Refresh on Page Size
  useEffect(() => {
    if (partnerId && !loading) {
      fetchCorrelationData(1);
      setCurrentPage(1);
    }
  }, [pageSize]);

  return (
    <div className="snowflake-dashboard partner-snowflake-tab animated fadeIn">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <h5 className="font-weight-bold mb-0">SnowFlake Insights</h5>

        <div>
          <Form
            initialValues={{ start_date: startDate, end_date: endDate }}
            enableReinitialize
          >
            <CustomDateFilter
              onRangeChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </Form>
        </div>
      </div>

      <div className="mt-4">
        <h5 className="font-weight-bold mb-3">
          Activity & Correlation Details
        </h5>
        <SnowFlakeAttribution
          attributionData={attributionData}
          correlateData={correlateData}
          corrStartDate={startDate}
          corrEndDate={endDate}
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={totalRecords}
          totalPages={Math.ceil(totalRecords / pageSize)}
          handlePageChange={setCurrentPage}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          loading={loading || tableLoading}
          isPartner={true}
        />
      </div>

      <SnowFlakeInsights
        partnerId={partnerId}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

export default SnowflakeTab;
